import { useState } from 'react'
import {
  Archive,
  CalendarClock,
  CheckCircle2,
  Link2,
  Loader2,
  Pencil,
  Send,
  Trash2,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTask, useSoftDeleteTask, useUpdateTask } from '@/hooks/use-tasks'
import { useChecklist, useChecklistMutations } from '@/hooks/use-checklist'
import { useComments, useAddComment } from '@/hooks/use-comments'
import { useAttachments, useUploadAttachment, useDeleteAttachment } from '@/hooks/use-attachments'
import { useActivity } from '@/hooks/use-activity'
import { useApprovals, useDecideApproval, useRequestApproval } from '@/hooks/use-approvals'
import { useMembers } from '@/hooks/use-members'
import { useWorkspace } from '@/providers/workspace-provider'
import { ETAPAS, STATUSES, LABELS } from '@/lib/constants'
import { formatDate, formatRelative, humanFileSize } from '@/lib/format'
import { getSignedUrl } from '@/lib/storage'
import { cn } from '@/lib/utils'
import type { PostEtapa, TaskStatus } from '@/types/database'
import type { TaskWithRelations } from '@/types/domain'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PageLoader, EmptyState } from '@/components/common/states'
import { FrenteBadge, PriorityBadge } from '@/components/common/badges'
import { UserAvatar } from '@/components/common/user-avatar'
import { missingBriefingFields } from './briefing'
import { deriveStations, ETAPA_TO_STATUS, type StationState } from './production'

function stationTone(state: StationState): string {
  if (state === 'done') return 'border-success/40 bg-success/10 text-success'
  if (state === 'curr') return 'border-warning/50 bg-warning/10 text-warning'
  if (state === 'late') return 'border-destructive/50 bg-destructive/10 text-destructive'
  if (state === 'empty') return 'border-dashed border-border/70 text-muted-foreground/50'
  return 'border-border/60 text-muted-foreground'
}

interface Props {
  taskId: string | null
  onClose: () => void
  onEdit: (task: TaskWithRelations) => void
}

export function TaskDetailSheet({ taskId, onClose, onEdit }: Props) {
  const { data: task, isLoading } = useTask(taskId)
  const { can } = useWorkspace()

  return (
    <Sheet open={!!taskId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        {isLoading || !task ? (
          <PageLoader />
        ) : (
          <>
            <SheetHeader>
              <div className="flex flex-wrap items-center gap-2 pr-8">
                <FrenteBadge frente={task.frente} />
                <PriorityBadge prioridade={task.prioridade} />
                <span className="text-xs text-muted-foreground">
                  {task.is_content ? LABELS.etapa[task.etapa ?? 'ideia'] : LABELS.tipo[task.tipo]}
                </span>
              </div>
              <SheetTitle className="pr-8 leading-snug">{task.title}</SheetTitle>
            </SheetHeader>

            <div className="flex items-center gap-2 px-5">
              {can.write && <StageQuickSelect task={task} />}
              {can.write && (
                <Button variant="outline" size="sm" onClick={() => onEdit(task)}>
                  <Pencil className="h-4 w-4" /> Editar
                </Button>
              )}
              {can.write && <ArchiveButton taskId={task.id} onDone={onClose} />}
            </div>

            <ScrollArea className="flex-1 px-5 pb-5">
              <Tabs defaultValue="detalhes" className="mt-2">
                <TabsList className="w-full">
                  <TabsTrigger value="detalhes" className="flex-1">
                    Detalhes
                  </TabsTrigger>
                  <TabsTrigger value="checklist" className="flex-1">
                    Checklist
                  </TabsTrigger>
                  <TabsTrigger value="comentarios" className="flex-1">
                    Comentários
                  </TabsTrigger>
                  <TabsTrigger value="anexos" className="flex-1">
                    Anexos
                  </TabsTrigger>
                  <TabsTrigger value="historico" className="flex-1">
                    Histórico
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="detalhes">
                  <DetailsTab task={task} />
                </TabsContent>
                <TabsContent value="checklist">
                  <ChecklistTab taskId={task.id} canWrite={can.write} />
                </TabsContent>
                <TabsContent value="comentarios">
                  <CommentsTab taskId={task.id} />
                </TabsContent>
                <TabsContent value="anexos">
                  <AttachmentsTab taskId={task.id} canWrite={can.write} />
                </TabsContent>
                <TabsContent value="historico">
                  <HistoryTab taskId={task.id} />
                </TabsContent>
              </Tabs>
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

function StageQuickSelect({ task }: { task: TaskWithRelations }) {
  const update = useUpdateTask()
  if (task.is_content) {
    return (
      <Select
        value={task.etapa ?? 'ideia'}
        onValueChange={(v) =>
          update.mutate({
            id: task.id,
            patch: { etapa: v as PostEtapa, status: ETAPA_TO_STATUS[v as PostEtapa] },
          })
        }
      >
        <SelectTrigger className="h-9 w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ETAPAS.map((e) => (
            <SelectItem key={e.value} value={e.value}>
              {e.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
  return (
    <Select
      value={task.status}
      onValueChange={(v) => update.mutate({ id: task.id, patch: { status: v as TaskStatus } })}
    >
      <SelectTrigger className="h-9 w-48">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUSES.map((s) => (
          <SelectItem key={s.value} value={s.value}>
            {s.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function ArchiveButton({ taskId, onDone }: { taskId: string; onDone: () => void }) {
  const del = useSoftDeleteTask()
  const [confirm, setConfirm] = useState(false)
  if (!confirm) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={() => setConfirm(true)}
      >
        <Archive className="h-4 w-4" /> Arquivar
      </Button>
    )
  }
  return (
    <Button
      variant="destructive"
      size="sm"
      onClick={async () => {
        await del.mutateAsync(taskId)
        toast.success('Tarefa arquivada')
        onDone()
      }}
    >
      {del.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Trash2 className="h-4 w-4" />
      )}{' '}
      Confirmar
    </Button>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="col-span-2">{children}</span>
    </div>
  )
}

function DetailsTab({ task }: { task: TaskWithRelations }) {
  const briefing = missingBriefingFields(task)
  const links = Array.isArray(task.links) ? (task.links as { label?: string; url?: string }[]) : []
  return (
    <div className="space-y-4 py-2">
      {task.is_blocked && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <strong>Bloqueada:</strong> {task.block_reason}
        </div>
      )}
      {briefing.length > 0 && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 p-3 text-sm">
          <strong>Briefing incompleto.</strong> Falta: {briefing.join(', ')}.
        </div>
      )}
      {task.description && <p className="whitespace-pre-wrap text-sm">{task.description}</p>}

      <Separator />
      <div>
        <Row label="Status">{LABELS.status[task.status]}</Row>
        <Row label="Pilar">{task.pilar ? LABELS.pilar[task.pilar] : '—'}</Row>
        <Row label="Responsável">
          <PersonInline p={task.assignee} />
        </Row>
        <Row label="Aprovador">
          <PersonInline p={task.approver} />
        </Row>
        {task.is_content && (
          <>
            <Row label="Formato">{task.format ?? '—'}</Row>
            <Row label="Canais">{task.canais.length ? task.canais.join(', ') : '—'}</Row>
            <Row label="Apresentador">{task.apresentador ?? '—'}</Row>
            <Row label="Revisor">{task.revisor ?? '—'}</Row>
          </>
        )}
        <Row label="Próxima ação">{task.next_action ?? '—'}</Row>
      </div>

      {task.is_content ? (
        <>
          <Separator />
          <div className="space-y-2">
            <p className="text-sm font-medium">Pipeline de produção</p>
            <div className="grid grid-cols-3 gap-2">
              {deriveStations(task).map((st) => (
                <div
                  key={st.key}
                  className={cn(
                    'rounded-lg border p-2 text-center',
                    stationTone(st.state),
                    st.state === 'curr' && 'animate-pulse',
                  )}
                >
                  <p className="text-[10px] font-bold tracking-wide">{st.label}</p>
                  <p className="tnum text-[10px] opacity-80">
                    {st.date ? formatDate(st.date) : '—'}
                  </p>
                </div>
              ))}
            </div>
            {task.horario && (
              <p className="text-xs text-muted-foreground">
                Horário de postagem: <span className="tnum">{task.horario.slice(0, 5)}</span>
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <Separator />
          <div>
            <Row label="Prazo">{formatDate(task.agreed_due_date)}</Row>
          </div>
        </>
      )}

      {(task.campaign || task.event) && (
        <>
          <Separator />
          <div>
            {task.campaign && <Row label="Campanha">{task.campaign.name}</Row>}
            {task.event && <Row label="Evento">{task.event.name}</Row>}
          </div>
        </>
      )}

      {links.length > 0 && (
        <>
          <Separator />
          <div className="space-y-1">
            {links.map((l, i) => (
              <a
                key={i}
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Link2 className="h-4 w-4" /> {l.label ?? l.url}
              </a>
            ))}
          </div>
        </>
      )}

      <Separator />
      <ApprovalSection task={task} />
    </div>
  )
}

function PersonInline({ p }: { p: TaskWithRelations['assignee'] }) {
  if (!p) return <span className="text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center gap-2">
      <UserAvatar profile={p} className="h-6 w-6" showTooltip={false} />
      {p.full_name}
    </span>
  )
}

function ApprovalSection({ task }: { task: TaskWithRelations }) {
  const { can } = useWorkspace()
  const { data: approvals = [] } = useApprovals()
  const request = useRequestApproval()
  const decide = useDecideApproval()
  const taskApprovals = approvals.filter((a) => a.task_id === task.id)
  const pending = taskApprovals.find((a) => a.status === 'pendente')

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Aprovações</p>
        {can.write && !pending && (
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              await request.mutateAsync({ taskId: task.id, approverId: task.approver_id })
              toast.success('Aprovação solicitada')
            }}
          >
            <Send className="h-3.5 w-3.5" /> Solicitar aprovação
          </Button>
        )}
      </div>
      {taskApprovals.length === 0 && (
        <p className="text-xs text-muted-foreground">Nenhuma aprovação registrada.</p>
      )}
      {taskApprovals.map((a) => (
        <div key={a.id} className="rounded-lg border border-border/70 p-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium">
              Rodada {a.round} · {LABELS.approval[a.status]}
            </span>
            <span className="text-xs text-muted-foreground">{formatRelative(a.created_at)}</span>
          </div>
          {a.comment && <p className="mt-1 text-xs text-muted-foreground">{a.comment}</p>}
          {a.status === 'pendente' && can.approve && (
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                onClick={() => decide.mutate({ id: a.id, taskId: task.id, status: 'aprovado' })}
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Aprovar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  decide.mutate({ id: a.id, taskId: task.id, status: 'ajustes_solicitados' })
                }
              >
                Pedir ajustes
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => decide.mutate({ id: a.id, taskId: task.id, status: 'reprovado' })}
              >
                Reprovar
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function ChecklistTab({ taskId, canWrite }: { taskId: string; canWrite: boolean }) {
  const { data: items = [], isLoading } = useChecklist(taskId)
  const { add, toggle, remove } = useChecklistMutations(taskId)
  const [label, setLabel] = useState('')
  const done = items.filter((i) => i.is_done).length

  if (isLoading) return <PageLoader />
  return (
    <div className="space-y-3 py-2">
      {items.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${(done / items.length) * 100}%` }}
            />
          </div>
          {done}/{items.length}
        </div>
      )}
      <ul className="space-y-1">
        {items.map((it) => (
          <li
            key={it.id}
            className="group flex items-center gap-2 rounded-lg px-1 py-1 hover:bg-accent/40"
          >
            <Checkbox
              checked={it.is_done}
              disabled={!canWrite}
              onCheckedChange={(v) => toggle.mutate({ id: it.id, done: !!v })}
            />
            <span
              className={cn('flex-1 text-sm', it.is_done && 'text-muted-foreground line-through')}
            >
              {it.label}
            </span>
            {canWrite && (
              <button
                onClick={() => remove.mutate(it.id)}
                className="opacity-0 transition-opacity group-hover:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canWrite && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (label.trim()) {
              add.mutate(label.trim())
              setLabel('')
            }
          }}
          className="flex gap-2"
        >
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Novo item…"
          />
          <Button type="submit" variant="outline">
            Adicionar
          </Button>
        </form>
      )}
    </div>
  )
}

function CommentsTab({ taskId }: { taskId: string }) {
  const { data: comments = [], isLoading } = useComments(taskId)
  const { data: members = [] } = useMembers()
  const add = useAddComment(taskId)
  const [body, setBody] = useState('')
  const [mentions, setMentions] = useState<string[]>([])

  if (isLoading) return <PageLoader />
  return (
    <div className="space-y-4 py-2">
      <ul className="space-y-3">
        {comments.length === 0 && <EmptyState title="Sem comentários ainda" className="border-0" />}
        {comments.map((c) => (
          <li key={c.id} className="flex gap-2">
            <UserAvatar profile={c.author} className="h-7 w-7" showTooltip={false} />
            <div className="flex-1 rounded-xl bg-muted/50 p-2.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{c.author?.full_name ?? 'Alguém'}</span>
                <span className="text-[11px] text-muted-foreground">
                  {formatRelative(c.created_at)}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm">{c.body}</p>
            </div>
          </li>
        ))}
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!body.trim()) return
          add.mutate({ body: body.trim(), mentions })
          setBody('')
          setMentions([])
        }}
        className="space-y-2"
      >
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Escreva um comentário…"
          rows={2}
        />
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-muted-foreground">Mencionar:</span>
          {members.map((m) => (
            <button
              key={m.user_id}
              type="button"
              onClick={() =>
                setMentions((prev) =>
                  prev.includes(m.user_id)
                    ? prev.filter((x) => x !== m.user_id)
                    : [...prev, m.user_id],
                )
              }
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs transition-colors',
                mentions.includes(m.user_id)
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground',
              )}
            >
              {m.profile?.full_name?.split(' ')[0] ?? '?'}
            </button>
          ))}
          <Button type="submit" size="sm" className="ml-auto" disabled={add.isPending}>
            {add.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}{' '}
            Enviar
          </Button>
        </div>
      </form>
    </div>
  )
}

function AttachmentsTab({ taskId, canWrite }: { taskId: string; canWrite: boolean }) {
  const { data: attachments = [], isLoading } = useAttachments(taskId)
  const upload = useUploadAttachment(taskId)
  const del = useDeleteAttachment(taskId)

  async function openSigned(path: string) {
    try {
      const url = await getSignedUrl(path)
      window.open(url, '_blank', 'noopener')
    } catch {
      toast.error('Não foi possível gerar o link do arquivo')
    }
  }

  if (isLoading) return <PageLoader />
  return (
    <div className="space-y-3 py-2">
      {canWrite && (
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border/70 p-4 text-sm text-muted-foreground hover:bg-accent/40">
          <Upload className="h-4 w-4" />
          {upload.isPending ? 'Enviando…' : 'Enviar arquivo (até 50 MB)'}
          <input
            type="file"
            className="hidden"
            disabled={upload.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file)
                upload.mutate(file, {
                  onError: (err) =>
                    toast.error(err instanceof Error ? err.message : 'Falha no upload'),
                })
              e.target.value = ''
            }}
          />
        </label>
      )}
      {attachments.length === 0 && <EmptyState title="Sem anexos" className="border-0" />}
      <ul className="space-y-1">
        {attachments.map((a) => (
          <li key={a.id} className="flex items-center gap-2 rounded-lg border border-border/60 p-2">
            <button
              onClick={() => openSigned(a.storage_path)}
              className="flex-1 truncate text-left text-sm text-primary hover:underline"
            >
              {a.file_name}
            </button>
            <span className="text-xs text-muted-foreground">{humanFileSize(a.size_bytes)}</span>
            {canWrite && (
              <button onClick={() => del.mutate(a)}>
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function HistoryTab({ taskId }: { taskId: string }) {
  const { data: logs = [], isLoading } = useActivity(taskId)
  if (isLoading) return <PageLoader />
  if (!logs.length)
    return <EmptyState icon={CalendarClock} title="Sem histórico" className="border-0" />
  return (
    <ul className="space-y-3 py-2">
      {logs.map((l) => (
        <li key={l.id} className="flex gap-3">
          <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
          <div className="flex-1">
            <p className="text-sm">{l.summary ?? l.action}</p>
            <p className="text-xs text-muted-foreground">
              {l.actor?.full_name ?? 'Sistema'} · {formatRelative(l.created_at)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  )
}
